import React from 'react';
import { Box, Typography } from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';

// Formats a duration in seconds to m:ss (e.g. 225 -> "3:45").
export function formatDuration(seconds) {
    const s = Math.max(0, Math.round(Number(seconds) || 0));
    if (!s) return '';
    const m = Math.floor(s / 60);
    const rem = s % 60;
    return `${m}:${String(rem).padStart(2, '0')}`;
}

// Compact video thumbnail with a play overlay and duration badge. Falls back to
// a branded gradient placeholder when Loom hasn't supplied a thumbnail yet.
export function LoomThumbnail({ thumbnailUrl, duration, width = 96, height = 56, onClick }) {
    const badge = formatDuration(duration);
    return (
        <Box
            onClick={onClick}
            sx={{
                position: 'relative',
                width,
                height,
                flexShrink: 0,
                borderRadius: '8px',
                overflow: 'hidden',
                cursor: onClick ? 'pointer' : 'default',
                background: thumbnailUrl
                    ? undefined
                    : 'linear-gradient(135deg, #1c2540 0%, #287bd6 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            {thumbnailUrl ? (
                <Box
                    component="img"
                    src={thumbnailUrl}
                    alt=""
                    loading="lazy"
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
            ) : (
                <OndemandVideoIcon sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 22 }} />
            )}
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(0,0,0,0.18)',
                }}
            >
                <Box
                    sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        bgcolor: 'rgba(255,255,255,0.92)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                    }}
                >
                    <PlayArrowRoundedIcon sx={{ fontSize: 18, color: '#1c2540', ml: '1px' }} />
                </Box>
            </Box>
            {badge ? (
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 3,
                        right: 3,
                        px: 0.5,
                        py: '1px',
                        borderRadius: '4px',
                        bgcolor: 'rgba(0,0,0,0.78)',
                        color: '#fff',
                        fontSize: '0.62rem',
                        fontWeight: 600,
                        lineHeight: 1.3,
                    }}
                >
                    {badge}
                </Box>
            ) : null}
        </Box>
    );
}

// Responsive 16:9 Loom embed. Loom public embed URL: loom.com/embed/<id>.
export function LoomEmbed({ videoId, title }) {
    if (!videoId) return null;
    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                pb: '56.25%', // 16:9 — reserves space to avoid layout shift (CLS)
                borderRadius: '10px',
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: '#000',
            }}
        >
            <Box
                component="iframe"
                src={`https://www.loom.com/embed/${encodeURIComponent(videoId)}`}
                title={title || 'Loom video'}
                loading="lazy"
                frameBorder="0"
                allowFullScreen
                sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
            />
        </Box>
    );
}

// Renders a single help article (a Loom video tutorial).
export default function HelpContent({ article }) {
    if (!article) return null;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {article.loomVideoId ? (
                <LoomEmbed videoId={article.loomVideoId} title={article.title} />
            ) : null}

            {article.summary ? (
                <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>{article.summary}</Typography>
            ) : null}
        </Box>
    );
}
